import { IllegalOperationException } from '../exceptions/illegal-operation-exception.js';
import { PassEventValidityRule } from '../models/serializable/open-access-pass/pass-event-validity-rule.js';
import {
  AddEventToSeatedOpenAccessProtocol, RemoveEventFromSeatedOpenAccessProtocol,
} from '../models/serializable/open-access-pass/seated-open-access-protocol-modifier.js';
import { StageEntry } from '../models/serializable/open-access-pass/stage.js';
import { SeatingOptions } from '../models/serializable/seating-options.js';
import { isNullOrUndefined } from './null-safety.js';
import { OpenAccessPassSeatHelper } from './open-access-pass-seat-helper.js';

export class OpenAccessStageEntryHelper implements OpenAccessPassSeatHelper {
  private constructor(
      private stageEntry: StageEntry | null,
      private eventValidityRule: PassEventValidityRule | null,
      private passVariationId: string | null,
  ) {}

  static forPass(args: {
    stageEntry: StageEntry,
    eventValidityRule: PassEventValidityRule,
    passVariationId: string,
  }): OpenAccessStageEntryHelper {
    return new OpenAccessStageEntryHelper(args.stageEntry, args.eventValidityRule, args.passVariationId);
  }

  static forTemplate(args: {
    stageEntry: StageEntry,
    eventValidityRule: PassEventValidityRule,
  }): OpenAccessStageEntryHelper {
    return new OpenAccessStageEntryHelper(args.stageEntry, args.eventValidityRule, null);
  }

  static empty(): OpenAccessStageEntryHelper {
    return new OpenAccessStageEntryHelper(null, null, null);
  }

  static findModifiedSeats(stageEntry: StageEntry): [eventId: string, seatId: number][] {
    const seats: [eventId: string, seatId: number][] = [];
    for(const modifier of stageEntry.modifiers.values()) {
      if(modifier instanceof AddEventToSeatedOpenAccessProtocol) {
        seats.push([modifier.eventId, modifier.seatingOptions.seatId]);
      }
    }
    return seats;
  }

  findModifiedSeats(): [eventId: string, seatId: number][] {
    return OpenAccessStageEntryHelper.findModifiedSeats(this.requireStageEntry());
  }

  setSourcePass({ stageEntry, eventValidityRule, passVariationId }: {
    stageEntry: StageEntry,
    eventValidityRule: PassEventValidityRule,
    passVariationId: string,
  }): void {
    this.stageEntry = stageEntry;
    this.eventValidityRule = eventValidityRule;
    this.passVariationId = passVariationId;
  }

  setSourceTemplate({ stageEntry, eventValidityRule }: {
    stageEntry: StageEntry,
    eventValidityRule: PassEventValidityRule,
  }): void {
    this.stageEntry = stageEntry;
    this.eventValidityRule = eventValidityRule;
    this.passVariationId = null;
  }

  getSeatForEvent(eventId: string): SeatingOptions | null {
    const stageEntry = this.requireStageEntry();
    let result: SeatingOptions | null = null;
    if(stageEntry.seatingOptions != null && this.isEventAssigned(eventId)) {
      result = stageEntry.seatingOptions;
    }

    const modifier = stageEntry.modifiers.get(eventId);
    if(modifier instanceof AddEventToSeatedOpenAccessProtocol) {
      result = modifier.seatingOptions;
    }
    else if(modifier instanceof RemoveEventFromSeatedOpenAccessProtocol) {
      result = null;
    }
    return result;
  }

  getSeatDistribution(assignedEventIds: Iterable<string>): Map<string, SeatingOptions> {
    const stageEntry = this.requireStageEntry();
    const result = new Map<string, SeatingOptions>();
    if(stageEntry.seatingOptions != null) {
      for(const eventId of assignedEventIds) {
        result.set(eventId, stageEntry.seatingOptions);
      }
    }

    for(const [eventId, modifier] of stageEntry.modifiers) {
      if(modifier instanceof AddEventToSeatedOpenAccessProtocol) {
        result.set(eventId, modifier.seatingOptions);
      }
      else if(modifier instanceof RemoveEventFromSeatedOpenAccessProtocol) {
        result.delete(eventId);
      }
    }
    return result;
  }

  addEvent(eventId: string, seatingOptions: SeatingOptions): void {
    const stageEntry = this.requireStageEntry();
    stageEntry.modifiers.delete(eventId);
    if(this.isEventAssigned(eventId) && stageEntry.seatingOptions?.seatId === seatingOptions.seatId) {
      return;
    }
    stageEntry.modifiers.set(eventId, AddEventToSeatedOpenAccessProtocol.create({ eventId, seatingOptions }));
  }

  undoAddEvent(eventId: string): void {
    const stageEntry = this.requireStageEntry();
    if(stageEntry.modifiers.get(eventId) instanceof AddEventToSeatedOpenAccessProtocol) {
      stageEntry.modifiers.delete(eventId);
    }
  }

  removeEvent(eventId: string): void {
    const stageEntry = this.requireStageEntry();
    if(!this.isEventAssigned(eventId)) {
      return;
    }
    stageEntry.modifiers.set(eventId, RemoveEventFromSeatedOpenAccessProtocol.create({ eventId }));
  }

  undoRemoveEvent(eventId: string): void {
    const stageEntry = this.requireStageEntry();
    const modifier = stageEntry.modifiers.get(eventId);
    if(isNullOrUndefined(modifier)) {
      return;
    }
    if(modifier instanceof RemoveEventFromSeatedOpenAccessProtocol) {
      stageEntry.modifiers.delete(eventId);
    }
  }

  isEventAssigned(eventId: string): boolean {
    const stageEntry = this.requireStageEntry();
    const eventValidityRule = this.eventValidityRule!;
    return this.passVariationId != null ?
      stageEntry.isEventAssignedToPass({ eventId, passVariationId: this.passVariationId, eventValidityRule }):
      stageEntry.isEventAssignedToTemplate({ eventId, eventValidityRule });
  }

  private requireStageEntry(): StageEntry {
    if(this.stageEntry == null || this.eventValidityRule == null) {
      throw new IllegalOperationException('Cannot use [OpenAccessStageEntryHelper] without a source attached');
    }
    return this.stageEntry;
  }
}
